import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { CheckCircle2, XCircle, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { StarRatingDisplay } from "./StarRating";

const statusBadge = {
  pending: <Badge className="bg-[#FACC15] text-[#1A2E1A] hover:bg-[#FACC15] rounded-full">En attente</Badge>,
  approved: (
    <Badge className="bg-[#7AC143] hover:bg-[#7AC143] text-white rounded-full gap-1">
      <CheckCircle2 className="w-3 h-3" /> Publié
    </Badge>
  ),
  rejected: <Badge className="bg-red-500 hover:bg-red-500 text-white rounded-full">Rejeté</Badge>,
};

function fmtDate(s) {
  try {
    return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "";
  }
}

function ReviewRow({ r, onApprove, onReject, onDelete }) {
  return (
    <TableRow data-testid={`admin-review-row-${r.id}`}>
      <TableCell className="align-top">
        <div className="flex items-start gap-3">
          {r.product_image_url && (
            <img
              src={r.product_image_url}
              alt=""
              className="w-12 h-12 rounded-lg object-cover shrink-0"
            />
          )}
          <div>
            <div className="font-semibold text-[#1A2E1A] leading-tight">{r.product_name || "—"}</div>
            <div className="text-xs text-[#4B634B] mt-1">{fmtDate(r.created_at)}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="flex items-center gap-2 mb-1">
          <StarRatingDisplay value={r.rating} size={14} />
          <span className="text-sm font-semibold">{r.rating}/5</span>
        </div>
        <div className="font-bold text-[#1A2E1A] leading-tight">{r.title}</div>
        <p className="text-sm text-[#4B634B] mt-1 max-w-md whitespace-pre-line">{r.comment}</p>
      </TableCell>
      <TableCell className="align-top">
        <div className="text-sm">{r.user_name || "—"}</div>
        <div className="text-xs text-[#4B634B]">{r.user_email}</div>
        {r.is_verified_purchase && (
          <Badge className="bg-[#7AC143]/10 text-[#7AC143] hover:bg-[#7AC143]/10 border border-[#7AC143]/30 rounded-full text-xs gap-1 mt-2">
            <ShieldCheck className="w-3 h-3" /> Achat vérifié
          </Badge>
        )}
      </TableCell>
      <TableCell className="align-top">{statusBadge[r.status] || r.status}</TableCell>
      <TableCell className="align-top text-right whitespace-nowrap">
        {r.status !== "approved" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onApprove(r)}
            title="Publier"
            data-testid={`admin-review-approve-${r.id}`}
          >
            <CheckCircle2 className="w-4 h-4 text-[#7AC143]" />
          </Button>
        )}
        {r.status !== "rejected" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onReject(r)}
            title="Rejeter"
            data-testid={`admin-review-reject-${r.id}`}
          >
            <XCircle className="w-4 h-4 text-[#FACC15]" />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(r)}
          title="Supprimer"
          data-testid={`admin-review-delete-${r.id}`}
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function ReviewTable({ items, onApprove, onReject, onDelete }) {
  if (items.length === 0) {
    return <div className="text-center py-12 text-[#4B634B]">Aucun avis dans cette catégorie.</div>;
  }
  return (
    <div className="bg-white border border-[#E5EAE5] rounded-2xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Produit</TableHead>
            <TableHead>Avis</TableHead>
            <TableHead>Auteur</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((r) => (
            <ReviewRow key={r.id} r={r} onApprove={onApprove} onReject={onReject} onDelete={onDelete} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ReviewsModeration() {
  const [reviews, setReviews] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/reviews");
      setReviews(r.data);
    } catch {
      toast.error("Impossible de charger les avis");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onApprove = async (r) => {
    try {
      await api.post(`/admin/reviews/${r.id}/approve`);
      toast.success("Avis publié");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const onReject = async (r) => {
    try {
      await api.post(`/admin/reviews/${r.id}/reject`);
      toast.success("Avis rejeté");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const onDelete = async (r) => {
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Avis supprimé");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const pending = reviews.filter((r) => r.status === "pending");
  const approved = reviews.filter((r) => r.status === "approved");
  const rejected = reviews.filter((r) => r.status === "rejected");

  return (
    <div data-testid="admin-reviews-moderation">
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-reviews-tab-pending">
            En attente {pending.length > 0 && <span className="ml-2 inline-block bg-[#FACC15] text-[#1A2E1A] text-xs rounded-full px-2 py-0.5 font-bold">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="admin-reviews-tab-approved">
            Publiés ({approved.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="admin-reviews-tab-rejected">
            Rejetés ({rejected.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-6">
          <ReviewTable items={pending} onApprove={onApprove} onReject={onReject} onDelete={onDelete} />
        </TabsContent>
        <TabsContent value="approved" className="mt-6">
          <ReviewTable items={approved} onApprove={onApprove} onReject={onReject} onDelete={onDelete} />
        </TabsContent>
        <TabsContent value="rejected" className="mt-6">
          <ReviewTable items={rejected} onApprove={onApprove} onReject={onReject} onDelete={onDelete} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
